/*
  What the transcription service accepts, in one place, because three layers have to agree on it.

  The browser chooses a container when it starts recording; the server forwards the bytes; the service
  decides whether to read them. If those three disagree the failure lands at the far end of a network
  round trip, after the person has already spoken — which is the worst possible place to discover a
  format mismatch and the reason this list is a shared constant rather than three sensible defaults.

  NO TRANSCODING, WHICH IS A LICENCE DECISION BEFORE IT IS AN ENGINEERING ONE. The obvious way to
  guarantee a format is to convert to one, and the obvious tool for that is ffmpeg — LGPL-2.1+, which
  `tools/license-check.cjs` blocks, because this repo's stated requirement is no copyleft at any stage.
  So the browser must produce something acceptable directly. That is a constraint, not a preference, and
  the whole shape below follows from it.

  MEASURED RATHER THAN ASSUMED: Chromium 141 in this repo's own Playwright build reports support for
  `audio/webm`, `audio/webm;codecs=opus` and `audio/mp4`, and NOT for `audio/wav`, `audio/mpeg`,
  `audio/ogg` or `audio/flac`. Anybody reasoning "just record a wav" would have been wrong. Firefox is
  documented to produce Ogg/Opus and Safari MP4/AAC, both on the accepted list — but neither was
  verified here, and that is recorded rather than glossed.
*/
/**
 * The accepted containers, MOST PREFERRED FIRST — and the order is about bytes.
 *
 * Opus at speech bitrates is roughly a fortieth the size of the same audio as WAV, which matters twice:
 * the ceiling below is a hard limit somebody speaking for a few minutes can genuinely hit in WAV, and
 * every byte is a byte uploaded from a laptop on a client's guest wifi. The codec-qualified types come
 * before their bare equivalents because a bare `audio/webm` lets the browser pick, and at least one
 * engine picks something larger.
 *
 * WAV is last and is deliberately still present: it is the universal fallback, and a recording that is
 * bulky beats a recording that cannot be made.
 */
export const AZURE_AUDIO_CONTAINERS = [
    { mimeType: 'audio/webm;codecs=opus', extension: 'webm' },
    { mimeType: 'audio/ogg;codecs=opus', extension: 'ogg' },
    { mimeType: 'audio/webm', extension: 'webm' },
    { mimeType: 'audio/ogg', extension: 'ogg' },
    { mimeType: 'audio/mp4', extension: 'mp4' },
    { mimeType: 'audio/wav', extension: 'wav' },
];
/**
 * The upload ceiling, in bytes.
 *
 * 25 MiB is the documented per-file limit for Azure OpenAI's transcription, and it is enforced on OUR
 * side of the wire as well as theirs — refusing locally costs nothing, while discovering it remotely
 * costs the upload, the round trip, and a person's patience after they have already spoken.
 *
 * What it means in practice depends entirely on the container the browser picked, which is why the
 * recorder checks as it goes rather than at the end: in Opus this is hours, in WAV it is about four
 * minutes, and a recorder that only checked on stop would let somebody talk for ten.
 */
export const MAX_AUDIO_BYTES = 25 * 1024 * 1024;
/* ------------------------------------------------------------------ *
 * A spoken conversation
 * ------------------------------------------------------------------ */
/**
 * Twenty-four kilohertz, mono — what the realtime surface reads and writes.
 *
 * HERE RATHER THAN IN THE SERVER PACKAGE because both ends need it and for opposite reasons: the
 * browser has to PRODUCE samples at this rate off a microphone, and the session payload has to
 * DECLARE it. Two copies of a sample rate is the kind of disagreement that makes everybody sound
 * like they are talking at double speed, with nothing in the logs to say why.
 */
export const REALTIME_SAMPLE_RATE = 24_000;
/**
 * How long a spoken session may run before the server closes it.
 *
 * THE FIRST LIMIT IN THIS PRODUCT THAT BOUNDS DURATION, and that is worth saying rather than
 * hiding. Every other one bounds the SIZE of a request — `MAX_AUDIO_BYTES` above, the 4,000-character
 * prompt, the roster ceiling — because until now nothing had any duration to bound. An open
 * microphone is billed by the minute and hears the room it is in, so it gets a number.
 *
 * Ten minutes is a long single answer and a short workshop, which is the intended shape: a session is
 * something you open to work through a question, not something you leave running.
 *
 * Shared for the reason `MAX_AUDIO_BYTES` is: the client shows it and the server enforces it, and a
 * timer that counted down to a different number than the one that fires would be worse than no timer.
 */
export const MAX_REALTIME_SESSION_MS = 10 * 60 * 1000;
/**
 * How long a session may hear nothing at all before it closes itself.
 *
 * The ceiling above catches a conversation that ran long; this catches a tab somebody walked away
 * from, which is the more likely of the two and the one that bills ten minutes for silence.
 */
export const REALTIME_IDLE_MS = 90 * 1000;
//# sourceMappingURL=audio.js.map